import { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import type { ColDef, ICellRendererParams, IGetRowsParams } from 'ag-grid-community';
import { RefreshCw, Trash2, CheckCircle, XCircle, Clock, Database, AlertTriangle, CalendarDays, ScanSearch } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchQueryStats, fetchQueryLogsPage, fetchQueryDates, clearQueryLogs } from '@/api/monitoring';
import type { QueryFilter, QueryLogEntry, QueryStats } from '@/api/monitoring';
import { useI18n } from '@/i18n';
import QueryDetailModal from './QueryDetailModal';
import { confirmToast } from '@/lib/notify';

const PAGE_SIZE = 100;

function StatusCell({ value }: ICellRendererParams) {
  const { t } = useI18n();
  return value === 'success'
    ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium text-xs"><CheckCircle size={13} />{t('monitoring.queries.statusSuccess')}</span>
    : <span className="inline-flex items-center gap-1 text-rose-600 font-medium text-xs"><XCircle size={13} />{t('monitoring.queries.statusError')}</span>;
}

function DurationCell({ value }: ICellRendererParams) {
  const ms  = Number(value);
  const cls = ms > 1000 ? 'text-rose-600 font-bold'
    : ms > 300 ? 'text-amber-600 font-semibold'
    : 'text-emerald-700';
  return <span className={`text-xs ${cls}`}>{ms} ms</span>;
}

function QueryCell({ value }: ICellRendererParams<QueryLogEntry>) {
  return (
    <span className="text-xs font-mono text-gray-600" title={value}>
      {value?.slice(0, 120)}{value?.length > 120 ? '…' : ''}
    </span>
  );
}

function ErrorCell({ value }: ICellRendererParams) {
  if (!value) return <span className="text-gray-300 text-xs">—</span>;
  return <span className="text-xs text-rose-600" title={value}>{value.slice(0, 80)}{value.length > 80 ? '…' : ''}</span>;
}

function EndpointCell({ data }: ICellRendererParams<QueryLogEntry>) {
  const endpoint = data?.endpoint;
  if (!endpoint) return <span className="text-gray-300 text-[10px]">—</span>;
  return (
    <span className="text-[11px] font-mono text-slate-600" title={`${data?.method ?? ''} ${endpoint}`.trim()}>
      {data?.method ? `${data.method} ` : ''}{endpoint}
    </span>
  );
}

function SourceCell({ value }: ICellRendererParams) {
  if (!value) return <span className="text-gray-300 text-[10px]">—</span>;
  return (
    <span className="text-[11px] font-mono text-slate-500" title={String(value)}>
      {String(value).split('/src/').pop() ?? String(value)}
    </span>
  );
}

function ServiceCell({ value }: ICellRendererParams) {
  if (!value) return <span className="text-gray-300 text-[10px]">—</span>;
  const isAi = value === 'ai';
  const isN8n = value === 'n8n';
  let cls = 'bg-blue-50 text-blue-700 border-blue-200';
  if (isAi) cls = 'bg-teal-50 text-teal-700 border-teal-200';
  if (isN8n) cls = 'bg-indigo-50 text-indigo-700 border-indigo-200';

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-tighter ${cls}`}>
      {value}
    </span>
  );
}

function buildColDefs(onDetail: (entry: QueryLogEntry) => void, t: (key: string, params?: Record<string, string | number>) => string): ColDef<QueryLogEntry>[] {
  return [
    {
      headerName: '',
      width: 44,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: ({ data }: ICellRendererParams<QueryLogEntry>) => data ? (
        <button
          onClick={() => onDetail(data)}
          title={t('monitoring.queries.detailTitle')}
          className="flex items-center justify-center h-full w-full text-gray-400 hover:text-blue-500 transition-colors"
        >
          <ScanSearch size={14} />
        </button>
      ) : null,
    },
    { field: 'id',           headerName: '#',           width: 70 },
    { field: 'service',      headerName: t('monitoring.queries.service'),    width: 65,  cellRenderer: ServiceCell },
    { field: 'started_at',   headerName: t('monitoring.queries.startedAt'),   width: 145 },
    { field: 'duration_ms',  headerName: t('monitoring.queries.duration'),    width: 80, cellRenderer: DurationCell },
    { field: 'row_count',    headerName: t('monitoring.queries.rows'),        width: 65 },
    { field: 'params_count', headerName: t('monitoring.queries.params'),      width: 60 },
    { field: 'status',       headerName: t('monitoring.queries.status'),      width: 90, cellRenderer: StatusCell },
    {
      field: 'buf_hit', headerName: t('monitoring.queries.cacheHit'), width: 85,
      cellRenderer: ({ value }: ICellRendererParams) =>
        value != null
          ? <span className="text-[11px] text-emerald-600 font-medium">{value.toLocaleString('tr-TR')}</span>
          : <span className="text-gray-300 text-[10px]">—</span>,
    },
    {
      field: 'buf_read', headerName: t('monitoring.queries.cacheMiss'), width: 85,
      cellRenderer: ({ value }: ICellRendererParams) =>
        value != null
          ? <span className={`text-[11px] font-medium ${value > 0 ? 'text-rose-600' : 'text-gray-400'}`}>{value.toLocaleString('tr-TR')}</span>
          : <span className="text-gray-300 text-[10px]">—</span>,
    },
    {
      headerName: t('monitoring.queries.hitRate'),
      width: 65,
      valueGetter: ({ data }: { data?: QueryLogEntry }) => {
        const hit  = data?.buf_hit  ?? null;
        const miss = data?.buf_read ?? null;
        if (hit == null || miss == null) return null;
        const total = hit + miss;
        return total === 0 ? null : Math.round((hit / total) * 100);
      },
      cellRenderer: ({ value }: ICellRendererParams) => {
        if (value == null) return <span className="text-gray-300 text-[10px]">—</span>;
        const cls = value >= 95 ? 'text-emerald-600' : value >= 80 ? 'text-amber-600' : 'text-rose-600';
        return <span className={`text-[11px] font-semibold ${cls}`}>{value}%</span>;
      },
    },
    {
      field: 'io_read_ms', headerName: t('monitoring.queries.ioMs'), width: 75,
      cellRenderer: ({ value }: ICellRendererParams) =>
        value != null
          ? <span className={`text-[11px] font-medium ${value > 10 ? 'text-amber-600' : 'text-gray-500'}`}>{Number(value).toFixed(1)}</span>
          : <span className="text-gray-300 text-[10px]">—</span>,
    },
    { field: 'query',        headerName: t('monitoring.queries.query'),       flex: 3,   cellRenderer: QueryCell },
    { field: 'error',        headerName: t('monitoring.queries.errorLabel'),  flex: 1,   cellRenderer: ErrorCell },
    { colId: 'endpoint',     headerName: 'Endpoint',    width: 230, cellRenderer: EndpointCell },
    { field: 'source',       headerName: 'Kaynak',      width: 210, cellRenderer: SourceCell },
  ];
}

function StatCard({
  icon, label, value, sub, active = false, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl p-4 flex items-center gap-3 text-left transition ${
        active
          ? 'bg-blue-50 ring-2 ring-blue-500 shadow-sm'
          : 'bg-white shadow-sm hover:bg-gray-50'
      }`}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600">{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </button>
  );
}

function todayStr() { return new Date().toISOString().slice(0, 10); }
function formatDate(iso: string) {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export default function QueriesTab() {
  const { t } = useI18n();
  const gridRef = useRef<AgGridReact<QueryLogEntry>>(null);

  const [stats,        setStats]        = useState<QueryStats | null>(null);
  const [total,        setTotal]        = useState<number | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [autoRefresh,  setAutoRefresh]  = useState(false);
  const [lastRefresh,  setLastRefresh]  = useState<Date | null>(null);
  const [dates,        setDates]        = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [gridReady,    setGridReady]    = useState(false);
  const [detailEntry,  setDetailEntry]  = useState<QueryLogEntry | null>(null);
  const [activeFilter, setActiveFilter] = useState<QueryFilter>('all');
  const selectedDateRef = useRef<string>('');
  const activeFilterRef = useRef<QueryFilter>('all');

  const makeDatasource = useCallback((date: string, filter: QueryFilter) => ({
    rowCount: undefined,
    getRows: async (params: IGetRowsParams) => {
      try {
        const limit = params.endRow - params.startRow;
        const data  = await fetchQueryLogsPage(date, params.startRow, limit, filter);
        setTotal(data.total);
        params.successCallback(data.logs, data.total);
      } catch {
        params.failCallback();
      }
    },
  }), []);

  const applyDatasource = useCallback((date: string, filter: QueryFilter) => {
    gridRef.current?.api?.setGridOption('datasource', makeDatasource(date, filter));
  }, [makeDatasource]);

  const loadStats = useCallback(async (date: string, filter: QueryFilter) => {
    const res = await fetchQueryStats(date, filter);
    setStats(res.stats);
    setTotal(res.total);
    setLastRefresh(new Date());
  }, []);

  /* İlk yükleme */
  useEffect(() => {
    setStatsLoading(true);
    fetchQueryDates().then((d) => {
      setDates(d);
      const today = todayStr();
      const date  = d.includes(today) ? today : (d[0] ?? today);
      selectedDateRef.current = date;
      setSelectedDate(date);
    }).finally(() => setStatsLoading(false));
  }, []);

  /* Tarih değişince istatistikleri yükle */
  useEffect(() => {
    if (!selectedDate) return;
    selectedDateRef.current = selectedDate;
    void loadStats(selectedDate, activeFilter);
  }, [selectedDate, activeFilter, loadStats]);

  /* Grid hazır olduğunda ve tarih değiştiğinde datasource bağla */
  useEffect(() => {
    if (!gridReady || !selectedDate) return;
    applyDatasource(selectedDate, activeFilter);
  }, [gridReady, selectedDate, activeFilter, applyDatasource]);

  /* Auto-refresh */
  useEffect(() => {
    if (!autoRefresh || !selectedDate) return;
    const t = setInterval(() => {
      loadStats(selectedDate, activeFilterRef.current);
      if (gridReady) {
        applyDatasource(selectedDate, activeFilterRef.current);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, loadStats, selectedDate, applyDatasource, gridReady]);

  useEffect(() => {
    activeFilterRef.current = activeFilter;
  }, [activeFilter]);

  const handleRefresh = () => {
    if (!selectedDate) return;
    loadStats(selectedDate, activeFilter);
    if (gridReady) {
      applyDatasource(selectedDate, activeFilter);
    }
  };

  const handleClear = async () => {
    if (!(await confirmToast(t('monitoring.queries.clearConfirmTitle', { date: formatDate(selectedDate) }), t('monitoring.queries.clearConfirmMessage')))) return;
    try {
      await clearQueryLogs(selectedDate);
      setStats(null);
      setTotal(0);
      applyDatasource(selectedDate, activeFilter);
      const d = await fetchQueryDates();
      setDates(d);
      toast.success(t('monitoring.queries.clearSuccess'));
    } catch {
      toast.error(t('monitoring.queries.clearError'));
    }
  };

  const errorRate = stats && stats.total > 0
    ? ((stats.errors / stats.total) * 100).toFixed(1)
    : '0';

  const toggleFilter = (filter: QueryFilter) => {
    setActiveFilter((prev) => prev === filter ? 'all' : filter);
  };

  const colDefs = buildColDefs(setDetailEntry, t);

  return (
    <div className="flex flex-col h-full gap-4 p-4 lg:p-6">
      {/* Gün seçici */}
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarDays size={15} className="text-gray-400" />
        <span className="text-sm text-gray-500">{t('monitoring.queries.dayLabel')}</span>
        <div className="flex gap-1 flex-wrap">
          {dates.length === 0 && (
            <span className="text-xs text-gray-400 px-3 py-1.5">{t('monitoring.queries.noRecords')}</span>
          )}
          {dates.map(d => (
            <button
              key={d}
              onClick={() => setSelectedDate(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                d === selectedDate
                  ? 'bg-blue-500 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'
              }`}
            >
              {formatDate(d)}
              {d === todayStr() && <span className="ml-1 opacity-70">{t('monitoring.queries.todaySuffix')}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* İstatistik kartları */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          <StatCard
            icon={<Database size={18} />}
            label={t('monitoring.queries.totalQueries')}
            value={stats.total}
            active={activeFilter === 'all'}
            onClick={() => setActiveFilter('all')}
          />
          <StatCard icon={<Clock size={18} />}       label={t('monitoring.queries.avgDuration')}     value={`${stats.avg_ms} ms`} />
          <StatCard
            icon={<Clock size={18} className={stats.long_running > 0 ? 'text-amber-600' : ''} />}
            label={t('monitoring.queries.longRunning')}
            value={stats.long_running}
            sub=">= 1000 ms"
            active={activeFilter === 'slow'}
            onClick={() => toggleFilter('slow')}
          />
          <StatCard icon={<Clock size={18} />}       label={t('monitoring.queries.maxDuration')}    value={`${stats.max_ms} ms`} />
          <StatCard icon={<CheckCircle size={18} />} label={t('monitoring.queries.totalRows')}  value={(stats.total_rows ?? 0).toLocaleString('tr-TR')} />
          <StatCard
            icon={<AlertTriangle size={18} className={stats.errors > 0 ? 'text-rose-500' : ''} />}
            label={t('monitoring.queries.errorLabel')}
            value={stats.errors}
            sub={t('monitoring.queries.errorRate', { rate: errorRate })}
            active={activeFilter === 'error'}
            onClick={() => toggleFilter('error')}
          />
        </div>
      )}

      {/* Quick Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 p-1 bg-gray-100/50 rounded-xl border border-gray-100">
          {(['be', 'ai', 'n8n'] as const).map(svc => (
            <button
              key={svc}
              onClick={() => toggleFilter(svc)}
              className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                activeFilter === svc
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
              }`}
            >
              {svc}
            </button>
          ))}
        </div>

        <div className="h-4 w-px bg-gray-200 mx-1" />

        <div className="text-xs text-gray-400">
          {lastRefresh && <span>{t('monitoring.queries.lastRefresh', { time: lastRefresh.toLocaleTimeString('tr-TR') })}</span>}
          {total !== null && <span className="ml-3 font-medium text-gray-500">{t('monitoring.queries.records', { count: total.toLocaleString('tr-TR') })}</span>}
          {activeFilter !== 'all' && (
            <span className="ml-3 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold border border-blue-100 text-blue-700 uppercase">
              {activeFilter}
            </span>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"
        >
          <RefreshCw size={14} /> {t('monitoring.queries.refresh')}
        </button>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <div
            onClick={() => setAutoRefresh(p => !p)}
            className={`relative w-10 h-5 rounded-full transition-colors ${autoRefresh ? 'bg-blue-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoRefresh ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
          <span className={autoRefresh ? 'text-blue-600 font-medium' : 'text-gray-500'}>
            {t('monitoring.queries.autoRefresh')}
          </span>
        </label>
        <button
          onClick={handleClear}
          className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 ml-auto"
        >
          <Trash2 size={14} /> {t('monitoring.queries.clearButton', { date: formatDate(selectedDate) })}
        </button>
      </div>

      {/* Grid */}
      <div className="ag-theme-quartz flex-1 min-h-0 rounded-xl overflow-hidden shadow-sm">
        <AgGridReact<QueryLogEntry>
          modules={[AllEnterpriseModule]}
          ref={gridRef}
          columnDefs={colDefs}
          rowModelType="infinite"
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={20}
          rowHeight={32}
          headerHeight={32}
          defaultColDef={{ sortable: false, resizable: true }}
          infiniteInitialRowCount={total ?? 0}
          loading={statsLoading}
          onGridReady={(e) => {
            setGridReady(true);
            if (selectedDateRef.current) {
              e.api.setGridOption('datasource', makeDatasource(selectedDateRef.current, activeFilterRef.current));
            }
          }}
          getRowStyle={(params) => {
            if (params.data?.status === 'error') return { background: '#fff1f2' };
            if ((params.data?.duration_ms ?? 0) > 1000) return { background: '#fffbeb' };
            return undefined;
          }}
        />
      </div>

      {detailEntry && (
        <QueryDetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} />
      )}
    </div>
  );
}
